import React, { useState, useRef, useEffect, useCallback } from 'react';
import { StubLabel } from 'Components/Texts';
import { classCombine } from 'Utils/Utils';
import './SelectField.css';

//Inspo from https://medium.com/lego-engineering/building-accessible-select-component-in-react-b61dbdf5122f

//Select Field Helpers:
const isSafari = () => {
  const chromeInAgent = navigator.userAgent.indexOf('Chrome') > -1;
  const safariInAgent = navigator.userAgent.indexOf('Safari') > -1;
  return safariInAgent && !chromeInAgent;
};

const registerOpenDropdownHandlers = ({ options, optionsLength, activeIndex, setActiveIndex, select, name }) => {
  const keyDownCallback = (e) => {
    e.preventDefault();
    switch (e.key) {
      case 'Up':
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex(activeIndex <= 0 ? optionsLength - 1 : activeIndex - 1);
        return;
      case 'Down':
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex(activeIndex + 1 === optionsLength ? 0 : activeIndex + 1);
        return;
      case 'Enter':
      case ' ': // Space
        e.preventDefault();
        select(options[activeIndex].value);
        return;
      case 'Esc':
      case 'Escape':
        e.preventDefault();
        select(false);
        return;
      case 'PageUp':
      case 'Home':
        e.preventDefault();
        setActiveIndex(0);
        return;
      case 'PageDown':
      case 'End':
        e.preventDefault();
        setActiveIndex(options.length - 1);
        return;
    }
  };

  const onClick = (e) => {
    //if clicked outside of dropdown, close dropdown
    if (!e.composedPath().find((e) => e.dataset && e.dataset.name === name + '-dropdown-root')) {
      e.preventDefault();
      select(false);
    }
  };

  document.addEventListener('keydown', keyDownCallback);
  document.addEventListener('click', onClick);

  /*returning function that will be called on clean-up
  i.e. when the dependency changes in the useEffect
*/
  return () => {
    document.removeEventListener('keydown', keyDownCallback);
    document.removeEventListener('click', onClick);
  };
};

const registerClosedDropdownHandlers = ({ setIsDropdownOpen }) => {
  const keyDownCallback = (e) => {
    switch (e.key) {
      case 'Up':
      case 'ArrowUp':
      case 'Down':
      case 'ArrowDown':
      case ' ': // Space
      case 'Enter':
        e.preventDefault();
        setIsDropdownOpen(true);
    }
  };
  document.addEventListener('keydown', keyDownCallback);

  /*returning function that will be called on clean-up
  i.e. when the dependency changes in the useEffect
*/
  return () => {
    document.removeEventListener('keydown', keyDownCallback);
  };
};

const useAccessibleDropdown = ({ options, value, handleChange, name, onBlur }) => {
  const [isDropdownOpen, setIsDropdownOpenInternal] = useState(false);
  const listRef = useRef();
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFocus, setIsFocus] = useState(false);
  const select = useCallback(
    (value = '') => {
      if (value) {
        //mocking event.target.value
        //TODO: update handleChange func to handle event obj or string
        handleChange && handleChange({ target: { value } });
      }
      setIsDropdownOpen(false);
    },
    [handleChange, setIsDropdownOpen]
  );

  const setIsDropdownOpen = useCallback(
    (isOpen = false) => {
      if (isOpen) {
        const selected = options.findIndex((o) => o.value === value);
        setActiveIndex(selected < 0 ? 0 : selected);
        if (listRef.current && isSafari()) {
          requestAnimationFrame(() => {
            listRef.current.focus();
          });
        }
      } else {
        onBlur();
        if (listRef.current && isSafari()) {
          requestAnimationFrame(() => {
            listRef.current.previousSibling.focus();
          });
        }
      }
      setIsDropdownOpenInternal(isOpen);
    },
    [onBlur, options, value]
  );

  useEffect(() => {
    if (isDropdownOpen) {
      return registerOpenDropdownHandlers({
        activeIndex,
        setActiveIndex,
        options,
        optionsLength: options.length,
        select,
        name,
      });
    } else if (isFocus) {
      return registerClosedDropdownHandlers({
        setIsDropdownOpen,
      });
    }
  }, [isDropdownOpen, activeIndex, isFocus, name, options, select, setIsDropdownOpen]);

  return {
    isDropdownOpen,
    setIsDropdownOpen,
    activeIndex,
    setActiveIndex,
    select,
    setIsFocus,
    listRef,
  };
};

const SelectListItem = ({ index, activeIndex, optionValue, name, setActiveIndex, chosen, label, select }) => {
  const listItemRef = useRef();

  if (index === activeIndex) {
    listItemRef?.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }

  return (
    <li
      ref={listItemRef}
      className="selectOption"
      id={`${name}_element_${optionValue}`}
      aria-selected={index === activeIndex}
      role="option"
    >
      <label className="selectOptionVal" onMouseOver={() => setActiveIndex(index)}>
        <input
          type="radio"
          name={`${name}_radio`}
          value={optionValue}
          className={classCombine('optionRadio', chosen?.value === optionValue && 'checked')}
          checked={chosen?.value === optionValue}
          /* using onClick instead of onChange so will still select and close if click on the already
          selected value. Keeping onChange to prevent console warning for input radio element.
          Keeping input radio element as supposed to be better for accessibility */
          onChange={() => null}
          onClick={() => {
            select(optionValue);
          }}
        />
        <span>{label}</span>
        <div></div>
      </label>
    </li>
  );
};

export const SelectField = ({
  name,
  value = '',
  onBlur,
  handleChange,
  label,
  ariaLabel,
  isDisabled,
  error,
  touched,
  className = '',
  options = [], // [{ value: string, label: string}]
}) => {
  let inputClassName = `formFieldInput ${touched && error ? 'inputError' : ''}`;
  inputClassName = `${inputClassName} ${value || isDisabled ? 'hasValue' : ''}`;

  const labelId = label && !ariaLabel ? `selectField-${label.replace(/\s+/g, '')}` : null;

  const { isDropdownOpen, setIsDropdownOpen, activeIndex, setActiveIndex, select, setIsFocus, listRef } =
    useAccessibleDropdown({ options, value, handleChange, name, onBlur });
  const chosen = options.find((o) => o?.value === value);

  return (
    <div className={`formField formFieldSelect ${className}`} data-name={`${name}-dropdown-root`}>
      <div className="animatedFieldContainer">
        <button
          name={name}
          type="button"
          className={`${inputClassName} selectButton`}
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          onFocus={() => setIsFocus(true)}
          onBlur={() => setIsFocus(false)}
          role="combobox"
          aria-autocomplete="none"
          aria-label={label}
          aria-labelledby={labelId}
          aria-haspopup="listbox"
          aria-controls={`${name}_dropdown`}
          aria-expanded={isDropdownOpen}
          aria-activedescendant={`${name}_element_${value}`}
        >
          <span>{chosen?.label}</span>
          <img
            className={`selectChevron ${isDropdownOpen && 'selectButtonOpen'}`}
            alt="chevron"
            src="/account/static/images/select-chevron.svg"
          />
        </button>
        <StubLabel text={label} id={labelId} className="animatedSelectLabel" />
        <ul
          className={classCombine('selectDropdown', !isDropdownOpen && 'selectIsClosed')}
          ref={listRef}
          role="listbox"
          id={`${name}_dropdown`}
          tabIndex={-1}
        >
          {options.map(({ label, value: optionValue }, index) => (
            <SelectListItem
              key={optionValue}
              index={index}
              label={label}
              activeIndex={activeIndex}
              optionValue={optionValue}
              name={name}
              setActiveIndex={setActiveIndex}
              chosen={chosen}
              select={select}
            />
          ))}
        </ul>
        {touched && error && <div className="formFieldError">{error}</div>}
      </div>
    </div>
  );
};
